import { createElement as rc, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled, View, Text, webOnlyStyles, fromTheme } from 'lib_ui-primitives';
import useDisabledForNetwork from '../../../hooks/useDisabledForNetwork';
import useHiddenForValue from '../../../hooks/useHiddenForValue';
import { useSelection } from './Selection';
import {
    useTabHover,
    useTabFocus,
    useTabClickHandler,
    useKeyboardNavigation,
    getAccessibilityProps
} from './Tab.gl2025.shared';

// Web tab container with underline style
let TabContainer = styled(View).attrs({ name: 'tab-gl2025', role: 'tab' })`
    display: ${({ displayed }) => (displayed ? 'flex' : 'none')};
    flex-direction: row;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    user-select: none;
    
    /* Reset borders and remove default browser focus outline */
    border: none;
    outline: none;
    
    /* Dynamic styles based on state */
    height: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'height')({ theme })};
    padding-top: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingTop')({ theme })};
    padding-bottom: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingBottom')({ theme })};
    padding-left: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingLeft')({ theme })};
    padding-right: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'container', 'paddingRight')({ theme })};
    
    /* Bottom border for selected/hover state - only for current (selected) tabs when not focused */
    ${({ mode, theme }) =>
        mode.includes('current') && !mode.includes('focus')
            ? `
                border-bottom-width: ${fromTheme('tabs', ...mode, 'container', 'borderBottomWidth')({ theme })};
                border-bottom-color: ${fromTheme('tabs', ...mode, 'container', 'borderBottomColor')({ theme })};
                border-bottom-style: solid;
            `
            : ''
    }
    
    /* Full border with radius - only for focus state */
    ${({ mode, theme }) =>
        mode.includes('focus')
            ? `
                border-width: ${fromTheme('tabs', ...mode, 'container', 'borderWidth')({ theme })};
                border-color: ${fromTheme('tabs', ...mode, 'container', 'borderColor')({ theme })};
                border-style: solid;
                border-radius: ${fromTheme('tabs', ...mode, 'container', 'borderRadius')({ theme })};
                box-shadow: ${fromTheme('tabs', ...mode, 'container', 'boxShadow')({ theme })};
            `
            : ''
    }`;

TabContainer = webOnlyStyles(TabContainer)`
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

TabContainer.displayName = 'TabContainer';

const TabText = styled(Text).attrs({ name: 'tab-text' })`
    white-space: nowrap;
    
    color: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'color')({ theme })};
    font-size: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'fontSize')({ theme })};
    font-weight: ${({ mode, theme }) => {
        let value = theme.tabs;
        for (const key of mode) {
            value = value?.[key];
        }
        return value?.text?.fontWeight;
    }};
    line-height: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'lineHeight')({ theme })};
    font-family: ${({ mode, theme }) => fromTheme('tabs', ...mode, 'text', 'fontFamily')({ theme })};
    opacity: ${({ mode, theme }) => {
        let value = theme.tabs;
        for (const key of mode) {
            value = value?.[key];
        }
        return value?.text?.opacity;
    }};
`;

TabText.displayName = 'TabText';

/**
 * @typedef {Object} Props
 * @property {Object} childHNode
 * @property {Function} onClick
 * @property {number} tabIndex
 * @property {boolean} selected
 * @property {boolean} displayed
 * @property {number} calcedMaxWidth
 */
/** @type {import('react').FC<Props>} */
function TabGL2025(props) {
    const { childHNode, onClick, tabIndex, selected, displayed, calcedMaxWidth, totalTabs, disabled: disabledProp } = props || {};
    const [, , setIfIndexDisabled, setIfIndexHidden] = useSelection();
    const { disabledByNetwork, hiddenByNetwork, disabledMessage } = useDisabledForNetwork(childHNode);
    
    const hiddenForValue = useHiddenForValue(childHNode);
    const hidden = hiddenByNetwork || hiddenForValue;
    const disabled = disabledProp || disabledByNetwork;
    
    // Hover and focus state management
    const [isHovered, handleMouseEnter, handleMouseLeave] = useTabHover();
    const [isFocused, handleFocus, handleBlur, handleMouseDown] = useTabFocus();
    
    // Click and keyboard handlers
    const handleClick = useTabClickHandler(onClick, disabled, tabIndex);
    const handleKeyDown = useKeyboardNavigation(tabIndex, totalTabs, onClick);
    
    // Update selection context with disabled/hidden state
    useEffect(() => {
        setIfIndexHidden(tabIndex, hidden);
    }, [setIfIndexHidden, hidden, tabIndex]);
    
    useEffect(() => {
        if (disabled) {
            setIfIndexDisabled(tabIndex, true);
        }
    }, [setIfIndexDisabled, disabled, tabIndex]);
    
    if (hidden) return null;
    
    // Determine current state for styling (mode path for theme access)
    const mode = disabled 
        ? ['disabled']
        : selected
        ? isFocused
            ? ['current', 'focus']
            : isHovered
            ? ['current', 'hover']
            : ['current', 'default']
        : isFocused
        ? ['inactive', 'focus']
        : isHovered
        ? ['inactive', 'hover']
        : ['inactive', 'default'];
    
    // Accessibility props
    const a11yProps = getAccessibilityProps(selected, childHNode?.id, tabIndex);
    
    return rc(
        TabContainer,
        {
            ...a11yProps,
            mode,
            disabled,
            displayed,
            isCurrent: selected,
            onClick: handleClick,
            onKeyDown: handleKeyDown,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onMouseDown: handleMouseDown,
            onFocus: handleFocus,
            onBlur: handleBlur,
            style: { maxWidth: calcedMaxWidth },
            title: disabled ? disabledMessage : childHNode?.title
        },
        rc(
            TabText,
            {
                mode,
                numberOfLines: 1,
                ellipseMode: 'tail'
            },
            childHNode?.title
        )
    );
}

TabGL2025.defaultProps = {};

TabGL2025.propTypes = {
    childHNode: PropTypes.object,
    onClick: PropTypes.func,
    tabIndex: PropTypes.number,
    selected: PropTypes.bool,
    displayed: PropTypes.bool,
    calcedMaxWidth: PropTypes.number,
    totalTabs: PropTypes.number
};

export default TabGL2025;
